import { ApiObservationAudit } from '../api'
import { CompanyMember } from './company'
import { NullablePartial, SortDirections } from '../tools'

const DIRECTIONS = ['ingate', 'outgate'] as const

export type Direction = (typeof DIRECTIONS)[number]

export const isDirection = (value: unknown): value is Direction => {
    const set = new Set<string>(DIRECTIONS)
    if (typeof value !== 'string') return false
    return set.has(value)
}

export type DamageType = 'None' | 'Light' | 'Severe'

export type GateObservation = {
    observationId: string
    loadDamage: DamageType
    vehicleDamage: DamageType
    chassisDamage: DamageType
} & NullablePartial<{
    DATE?: string
    TIME?: string
    siteId?: string
    gateId: string
    timezone: string
    assetTimestamp?: string | null | undefined
    assetType: string
    assetTypeId?: string | null | undefined
    assetDirection: Direction
    assetPreviewUrl?: string | undefined | null
    assetImageUrls?: string[]
    assetImagePaths?: string[]
    vehicleId?: string | undefined | null
    vehicleLicensePlate?: string | null | undefined
    vehicleNumber?: string | null | undefined
    vehicleDotNumber?: string | null | undefined
    // vehicleState?: string | null | undefined
    vehicleType?: string | null | undefined
    vehicleTypeId?: string | null | undefined
    vehicleCarrierId?: string | null | undefined
    vehicleCarrierName?: string | null | undefined
    vehicleOutwardObservationId?: string | null | undefined
    vehicleOutwardObservation?: any | null
    // vehicleColor?: string | undefined | null
    // vehicleColorId?: string | undefined | null
    vehicleDamage?: string | null
    vehicleRemarks?: null | string
    vehicleWeightClass?: null | string
    vehicleFuelType?: null | string
    vehicleDwellTimeHr?: number | null
    vehicleIsPresent?: boolean
    hasSeal: boolean | string | null
    sealNumber?: string | null

    chassisId?: string | null
    chassisNumber?: string | null
    chassisLicensePlate?: string | null
    // chassisState?: string | null
    chassisType?: string | null | undefined
    chassisTypeId?: string | null | undefined
    // chassisColor?: string | null
    // chassisColorId?: string | null
    chassisOutwardObservationId?: string | null
    chassisOutwardObservation?: any | null
    chassisDamage?: string | null
    chassisRemarks?: string | null
    chassisDwellTimeHr?: number | null
    chassisIsPresent?: boolean

    // loadColor?: string | null
    loadLicensePlate?: string | null
    // loadState?: string | null
    loadType?: string | null | undefined
    // loadColorId?: string | null
    loadTypeId?: string | null | undefined
    loadNumber?: string | null
    loadId?: string | null
    loadOutwardObservationId?: string | null
    loadOutwardObservation?: any | null
    loadRemarks?: string | null
    loadDwellTimeHr?: number | null
    loadCustomerName?: string | null
    loadIsPresent?: boolean

    // FIXME: where are these on the schema?
    isBare?: boolean
    loadOwner?: string

    meta?: any
    tags?: string[]
}>

export type YardMetadata = {
    report: {
        containers: number
        bareChassis: number
        totalChassis: number
        refeer: number
        chassis: number
        tractor: number
    }
    total: number
    page: number
    size: number
}

export type ObservationKey = keyof GateObservation

export const OBSERVATION_KEYS: { [key in ObservationKey]: ObservationKey } = {
    DATE: 'DATE',
    TIME: 'TIME',
    timezone: 'timezone',
    // vehicleColorId: 'vehicleColorId',
    vehicleTypeId: 'vehicleTypeId',
    // loadColorId: 'loadColorId',
    vehicleId: 'vehicleId',
    vehicleCarrierId: 'vehicleCarrierId',
    vehicleRemarks: 'vehicleRemarks',
    vehicleOutwardObservationId: 'vehicleOutwardObservationId',
    loadOutwardObservationId: 'loadOutwardObservationId',
    chassisOutwardObservationId: 'chassisOutwardObservationId',
    // chassisColorId: 'chassisColorId',
    isBare: 'isBare',
    siteId: 'siteId',
    gateId: 'gateId',
    assetPreviewUrl: 'assetPreviewUrl',
    assetTimestamp: 'assetTimestamp',
    assetType: 'assetType',
    assetDirection: 'assetDirection',
    vehicleLicensePlate: 'vehicleLicensePlate',
    vehicleNumber: 'vehicleNumber',
    vehicleDotNumber: 'vehicleDotNumber',
    // vehicleState: 'vehicleState',
    vehicleCarrierName: 'vehicleCarrierName',
    vehicleType: 'vehicleType',
    // vehicleColor: 'vehicleColor',
    vehicleDamage: 'vehicleDamage',
    vehicleDwellTimeHr: 'vehicleDwellTimeHr',
    vehicleIsPresent: 'vehicleIsPresent',
    vehicleWeightClass: 'vehicleWeightClass',
    vehicleFuelType: 'vehicleFuelType',
    loadType: 'loadType',
    loadTypeId: 'loadTypeId',
    loadLicensePlate: 'loadLicensePlate',
    // loadState: 'loadState',
    loadId: 'loadId',
    loadNumber: 'loadNumber',
    sealNumber: 'sealNumber',
    loadOwner: 'loadOwner',
    // loadColor: 'loadColor',
    loadDamage: 'loadDamage',
    loadCustomerName: 'loadCustomerName',
    loadDwellTimeHr: 'loadDwellTimeHr',
    loadRemarks: 'loadRemarks',
    loadIsPresent: 'loadIsPresent',
    hasSeal: 'hasSeal',
    chassisType: 'chassisType',
    chassisTypeId: 'chassisTypeId',
    chassisId: 'chassisId',
    chassisLicensePlate: 'chassisLicensePlate',
    chassisNumber: 'chassisNumber',
    chassisOutwardObservation: 'chassisOutwardObservation',
    loadOutwardObservation: 'loadOutwardObservation',
    vehicleOutwardObservation: 'vehicleOutwardObservation',
    // chassisState: 'chassisState',
    // chassisColor: 'chassisColor',
    chassisDamage: 'chassisDamage',
    chassisDwellTimeHr: 'chassisDwellTimeHr',
    chassisRemarks: 'chassisRemarks',
    observationId: 'observationId',
    chassisIsPresent: 'chassisIsPresent',
    assetTypeId: 'assetTypeId',
    assetImageUrls: 'assetImageUrls',
    assetImagePaths: 'assetImagePaths',
    meta: 'meta',
    tags: 'tags',
}

export const TABLE_KEYS = {
    ...OBSERVATION_KEYS,
    EDIT_OPTIONS: 'EDIT_OPTIONS',
    DATE: 'DATE',
    TIME: 'TIME',
} as const

export type GateFeedColumnKey = keyof typeof TABLE_KEYS

const keySet = new Set(Object.keys(TABLE_KEYS))

export const isObservationKey = (key: string): key is ObservationKey => keySet.has(key)

export const OBSERVATION_SECTION_KEYS = {
    ASSET: 'ASSET',
    VEHICLE: 'VEHICLE',
    CONTAINER: 'CONTAINER',
    CHASSIS: 'CHASSIS',
    OPERATIONS: 'OPERATIONS',
} as const
export type ObservationSectionKey = keyof typeof OBSERVATION_SECTION_KEYS
export type SortingAction = (
    params: { column: ObservationKey; direction: SortDirections } | null
) => void
export type GetCurrentSort = () => SortDirections | null
export type SortingReturn = {
    getCurrentSort: GetCurrentSort
    onSelect: (nextDirection: SortDirections | null) => void
}
export type ObservationColumn = {
    label: string
    key: ObservationSectionKey
    columns: Array<{
        label: string
        key: GateFeedColumnKey
        altAccessor?: string
        sorting?: SortingReturn
        hasSort?: boolean
    }>
}

export type ObservationDraft = NullablePartial<{
    siteId: string
    gateId: string
    assetTimestamp: string
    timezone: string
    assetDirection: Direction
    assetTypeId: string

    loadNumber: string
    hasSeal: boolean
    sealNumber: string
    // loadColorId: string
    loadDamage: DamageType
    loadDwellTimeHr: number
    loadRemarks: string
    loadCustomerName: string
    loadLicensePlate: string
    loadState: string
    loadTypeId: string

    chassisNumber: string
    chassisDamage: DamageType
    chassisDwellTimeHr: number
    chassisLicensePlate: string
    chassisState: string
    // chassisColorId: string
    chassisRemarks: string
    chassisTypeId: string

    vehicleLicensePlate: string
    vehicleNumber: string
    vehicleDotNumber: string
    vehicleState: string
    vehicleCarrierId: string
    vehicleTypeId: string
    // vehicleColorId: string
    vehicleDamage: DamageType
    vehicleRemarks: string
    vehicleWeightClass: string
    vehicleFuelType: string

    vehicleDwellTimeHr: number
}>

export type ObservationMetadata = NullablePartial<{
    gateId: string
    vehicleId: string
    chassisId: string
    loadId: string
    vehicleOutwardObservationId: string
    chassisOutwardObservationId: string
    loadOutwardObservationId: string
}>

export type ObservationDraftEdit = {
    draft: ObservationDraft
    meta: NullablePartial<{
        gateId: string
        vehicleId: string
        chassisId: string
        loadId: string
        vehicleOutwardObservationId: string
        chassisOutwardObservationId: string
        loadOutwardObservationId: string
    }>
}

export type ObservationOutgateData = NullablePartial<{
    id: string
    gateId: string
    assetTimestamp: string | null
    chassisId?: string | null
    loadId?: string | null
    vehicleId: string
}>

export type ObservationDraftKey = keyof ObservationDraft

const observationColumnKeySet = new Set<string>(Object.keys(TABLE_KEYS))

export const isObservationColumnKey = (key: string): key is GateFeedColumnKey =>
    observationColumnKeySet.has(key)

type ChangeAction = ApiObservationAudit['action']

export type ObservationChangeLog = {
    createdAt: string
    userId?: string
    action: ChangeAction
    comment?: string
    version: number
    observationid: string
    changes: Record<string, any>
}

export type ScheduledReport = {
    name: string
    phone: string
    email: string
    userId: string
}

export type Report = {
    type: 'yard' | 'dwell'
    schedule: {
        repeatTime?: string
        frequencyHrs?: number
    }
    subscribedUsers: Array<CompanyMember>
}

export const COLUMN_FILTER_SELECT_TYPE_IDS: Array<
    (typeof COLUMN_FILTER_PROPERTY_OPTIONS)[number]['value']
> = [
    // 'chassis.typeId',
    // 'vehicle.typeId',
    // 'load.typeId',
    // 'vehicle.carrierId'
]

export const COLUMN_FILTER_PROPERTY_OPTIONS = [
    {
        label: 'Carrier Name',
        value: 'vehicle.carrier.name',
    },
    {
        label: 'Load #',
        value: 'load.number',
    },
    {
        label: 'Load License Plate #',
        value: 'load.licensePlate',
    },
    {
        label: 'Load Type',
        value: 'load.type.name',
    },
    {
        label: 'Chassis #',
        value: 'chassis.number',
    },
    {
        label: 'Chassis License Plate #',
        value: 'chassis.licensePlate',
    },
    {
        label: 'Vehicle Truck #',
        value: 'vehicle.number',
    },
    {
        label: 'Vehicle DOT #',
        value: 'vehicle.dotNumber',
    },
    {
        label: 'Vehicle Class',
        value: 'vehicle.weightClass',
    },
    {
        label: 'Vehicle Fuel Type',
        value: 'vehicle.fuelType',
    },
    {
        label: 'Vehicle License Plate #',
        value: 'vehicle.licensePlate',
    },
    {
        label: 'Vehicle Type',
        value: 'vehicle.type.name',
    },
] as const

export const SECTION_KEYS = OBSERVATION_SECTION_KEYS

export type SectionKey = keyof typeof SECTION_KEYS
